import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import NewJobPage from "./pages/NewJobPage";
import UploadListPage from "./pages/UploadListPage";
import FirecrawlPage from "./pages/FirecrawlPage";
import JobDetailsPage from "./pages/JobDetailsPage";
import ActivityPage from "./pages/ActivityPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import DashboardLayout from "./components/DashboardLayout";

const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false }) => {
  const { user, loading, isSuperAdmin } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#006994] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  
  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOnly && user.role !== "admin" && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Wrapper for login route to handle redirect after login
const LoginRoute = () => {
  const { user } = useAuth();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";
  
  if (user) {
    return <Navigate to={from} replace />;
  }
  return <LoginPage />;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#006994] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout>
            <DashboardPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/jobs/new" element={
        <ProtectedRoute>
          <DashboardLayout>
            <NewJobPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/jobs/upload" element={
        <ProtectedRoute>
          <DashboardLayout>
            <UploadListPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/jobs/firecrawl" element={
        <ProtectedRoute>
          <DashboardLayout>
            <FirecrawlPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/jobs/:jobId" element={
        <ProtectedRoute>
          <DashboardLayout>
            <JobDetailsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/downloads" element={<Navigate to="/activity" replace />} />

      <Route path="/activity" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ActivityPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <DashboardLayout>
            <SettingsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <UsersPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/analytics" element={
        <ProtectedRoute adminOnly>
          <DashboardLayout>
            <AnalyticsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/organizations" element={
        <ProtectedRoute superAdminOnly>
          <DashboardLayout>
            <OrganizationsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#18181B',
              border: '1px solid #27272A',
              color: '#fff',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
